import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { useAuthContext } from '../contexts/AuthContext';
import { isOrgAdmin } from '../lib/orgRoles';
import api from '../lib/api';
import {
  PersonaGeneratePreview,
  type PersonaCompleteness,
  type PreviewPersona,
} from '../components/PersonaGeneratePreview';
import { PersonaAiBadge } from '../components/PersonaAiBadge';
import { CrmPersonaBadge } from '../components/crm/CrmPersonaBadge';
import { CrmDealPicker } from '../components/crm/CrmDealPicker';
import { isCrmPersona } from '../lib/personaSource';
import { generateAggregatePersonas, saveCrmPersonas, getCrmStatus } from '../lib/crmApi';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { LoadingQuotesDialog } from '../components/LoadingQuotesDialog';
import { PersonaVoiceVideoFields, type VoiceOption } from '../components/PersonaVoiceVideoFields';

interface Persona {
  id: string;
  name: string;
  jobTitle: string;
  type: string;
  agenda: string;
  typicalObjections: string;
  enabled: boolean;
  source?: string;
  orgId?: string | null;
  gender?: string | null;
  voice?: string | null;
  voiceProvider?: string | null;
  videoAvatarId?: string | null;
}

type PersonaEditForm = PreviewPersona & {
  voice?: string | null;
  voiceProvider?: string | null;
};

interface VideoAvatar {
  id: string;
  name: string;
  previewImageUrl: string | null;
  gender: string | null;
}

const SECTION_LINKS: {
  key: keyof PersonaCompleteness['sections'];
  label: string;
}[] = [
  { key: 'company', label: 'Company' },
  { key: 'product', label: 'Product' },
  { key: 'sales', label: 'Sales' },
  { key: 'competitors', label: 'Competitors' },
];

export function PersonasPage() {
  const { user } = useAuthContext();
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [completeness, setCompleteness] = useState<PersonaCompleteness | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewPersonas, setPreviewPersonas] = useState<PreviewPersona[]>([]);
  const [previewCompleteness, setPreviewCompleteness] = useState<PersonaCompleteness | null>(null);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editMode, setEditMode] = useState<'ai' | 'crm' | null>(null);
  const [editForm, setEditForm] = useState<PersonaEditForm | null>(null);
  const [crmConnected, setCrmConnected] = useState(false);
  const [hasDealFeature, setHasDealFeature] = useState(false);
  const [showDealPicker, setShowDealPicker] = useState(false);
  const [generatingAggregate, setGeneratingAggregate] = useState(false);
  const [pendingGenerate, setPendingGenerate] = useState(false);
  const [personaIdToDelete, setPersonaIdToDelete] = useState<string | null>(null);
  const [videoAvatars, setVideoAvatars] = useState<VideoAvatar[]>([]);
  const [voiceProvider, setVoiceProvider] = useState('openai');
  const [voiceOptions, setVoiceOptions] = useState<VoiceOption[]>([]);
  const isAdmin = isOrgAdmin(user?.orgRole);

  const loadPersonas = useCallback(() => {
    const endpoint = isAdmin ? '/orgs/me/personas?includeDisabled=true' : '/orgs/me/personas';
    return api.get(endpoint).then((res) => setPersonas(res.data));
  }, [isAdmin]);

  const loadCompleteness = useCallback(() => {
    if (!isAdmin) return Promise.resolve();
    return api
      .get('/orgs/me/personas/completeness')
      .then((res) => setCompleteness(res.data))
      .catch(() => setCompleteness(null));
  }, [isAdmin]);

  useEffect(() => {
    loadPersonas().catch(() => {});
    if (isAdmin) {
      api
        .get<VideoAvatar[]>('/orgs/me/video-avatars')
        .then((res) => setVideoAvatars(res.data))
        .catch(() => {});
      api
        .get<{ provider: string; voices: VoiceOption[] }>('/practice/tts/voices')
        .then((res) => {
          if (res.data?.provider) setVoiceProvider(res.data.provider);
          if (Array.isArray(res.data?.voices)) setVoiceOptions(res.data.voices);
        })
        .catch(() => {});
    }
  }, [loadPersonas, isAdmin]);

  useEffect(() => {
    loadCompleteness().catch(() => {});
  }, [loadCompleteness]);

  useEffect(() => {
    if (!isAdmin) return;
    api
      .get('/orgs/me')
      .then((res) => {
        const features: string[] = res.data.features ?? [];
        if (features.includes('crm_connect')) {
          getCrmStatus()
            .then((conns) => {
              setCrmConnected(conns.some((c) => c.status === 'active'));
            })
            .catch(() => {});
        }
        setHasDealFeature(features.includes('crm_deal_personas'));
      })
      .catch(() => {});
  }, [isAdmin]);

  const hasAiPersonas = personas.some((p) => p.source === 'ai_generated');

  const runGenerate = async () => {
    const res = await api.post('/orgs/me/personas/generate');
    setPreviewPersonas(res.data.personas);
    setPreviewCompleteness(res.data.completeness ?? null);
    setPreviewOpen(true);
  };

  const handleGenerateClick = () => {
    if (hasAiPersonas) {
      setPendingGenerate(true);
      return;
    }
    void runGenerateAfterConfirm();
  };

  const runGenerateAfterConfirm = async () => {
    setGenerating(true);
    try {
      await runGenerate();
      await loadCompleteness();
    } catch (err) {
      console.error('Generate personas failed', err);
      Sentry.captureException(err);
    } finally {
      setGenerating(false);
    }
  };

  const handlePreviewRegenerate = async () => {
    const res = await api.post('/orgs/me/personas/generate');
    setPreviewCompleteness(res.data.completeness ?? null);
    return {
      personas: res.data.personas as PreviewPersona[],
      completeness: res.data.completeness as PersonaCompleteness,
    };
  };

  const handleConfirmSave = async (rows: PreviewPersona[]) => {
    setSaving(true);
    try {
      await api.post('/orgs/me/personas/save-generated', { personas: rows });
      setPreviewOpen(false);
      await loadPersonas();
      await loadCompleteness();
    } catch (err) {
      console.error('Save personas failed', err);
      Sentry.captureException(err);
    } finally {
      setSaving(false);
    }
  };

  const togglePersona = async (personaId: string, enabled: boolean) => {
    try {
      if (enabled) {
        await api.delete(`/orgs/me/personas/${personaId}`);
      } else {
        await api.post('/orgs/me/personas', { personaId });
      }
      setPersonas((prev) =>
        prev.map((p) => (p.id === personaId ? { ...p, enabled: !enabled } : p)),
      );
    } catch (err) {
      console.error('Failed to toggle persona', err);
      Sentry.captureException(err);
    }
  };

  const personaMediaFromRow = (p: Persona) => ({
    voice: p.voice ?? null,
    voiceProvider: p.voiceProvider ?? null,
    videoAvatarId: p.videoAvatarId ?? null,
  });

  const startEditAi = (p: Persona) => {
    setEditingId(p.id);
    setEditMode('ai');
    setEditForm({
      name: p.name,
      jobTitle: p.jobTitle,
      type: p.type,
      agenda: p.agenda,
      typicalObjections: p.typicalObjections,
      gender: p.gender ?? null,
      ...personaMediaFromRow(p),
    });
  };

  const startEditCrm = (p: Persona) => {
    setEditingId(p.id);
    setEditMode('crm');
    setEditForm({
      name: p.name,
      jobTitle: p.jobTitle,
      type: p.type,
      agenda: p.agenda,
      typicalObjections: p.typicalObjections,
      gender: p.gender ?? null,
      ...personaMediaFromRow(p),
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditMode(null);
    setEditForm(null);
  };

  const buildPatchPayload = (form: PersonaEditForm) => ({
    name: form.name,
    jobTitle: form.jobTitle,
    type: form.type,
    agenda: form.agenda,
    typicalObjections: form.typicalObjections,
    gender: form.gender ?? null,
    voice: form.voice ?? null,
    voiceProvider: form.voice ? (form.voiceProvider ?? voiceProvider) : null,
    videoAvatarId: form.videoAvatarId ?? null,
  });

  const saveEdit = async () => {
    if (!editingId || !editForm) return;
    try {
      await api.patch(`/orgs/me/personas/${editingId}`, buildPatchPayload(editForm));
      cancelEdit();
      await loadPersonas();
    } catch (err) {
      console.error('Failed to update persona', err);
      Sentry.captureException(err);
    }
  };

  const confirmDeleteAiPersona = async () => {
    if (!personaIdToDelete) return;
    const personaId = personaIdToDelete;
    setPersonaIdToDelete(null);
    try {
      await api.delete(`/orgs/me/personas/generated/${personaId}`);
      await loadPersonas();
    } catch (err) {
      console.error('Failed to delete persona', err);
      Sentry.captureException(err);
    }
  };

  const handleAggregateGenerate = async () => {
    setGeneratingAggregate(true);
    try {
      const result = await generateAggregatePersonas();
      await saveCrmPersonas(result.personas, 'crm_aggregate');
      await loadPersonas();
    } catch (err) {
      console.error('Aggregate persona generation failed', err);
      Sentry.captureException(err);
    } finally {
      setGeneratingAggregate(false);
    }
  };

  const platformPersonas = personas.filter((p) => !p.orgId);
  const aiPersonas = personas.filter((p) => p.source === 'ai_generated');
  const crmPersonas = personas.filter((p) => isCrmPersona(p.source));
  const memberAiPersonas = !isAdmin ? personas.filter((p) => p.source === 'ai_generated') : [];
  const memberCrmPersonas = !isAdmin ? personas.filter((p) => isCrmPersona(p.source)) : [];
  const memberCatalogPersonas = !isAdmin
    ? personas.filter((p) => !p.orgId && !isCrmPersona(p.source))
    : [];

  const typePillClass = (type: string) =>
    type === 'champion'
      ? 'bg-green-100 text-green-700'
      : type === 'blocker'
        ? 'bg-red-100 text-red-700'
        : type === 'skeptic'
          ? 'bg-yellow-100 text-yellow-700'
          : 'bg-gray-100 text-gray-700';

  return (
    <div>
      <LoadingQuotesDialog
        open={generatingAggregate}
        title="Generating CRM personas"
        subtitle="Analyzing your CRM data with AI…"
      />
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Personas</h1>
        <p className="text-gray-600 mt-1">
          {isAdmin
            ? 'Manage which personas are available for your team, and generate AI personas from your business profile.'
            : 'Personas available for practice.'}
        </p>
      </div>

      {!isAdmin && (
        <div className="space-y-8 mb-8">
          {memberAiPersonas.length === 0 && memberCatalogPersonas.length === 0 ? (
            <p className="text-gray-500">
              No personas are enabled yet. Ask an admin to enable personas.
            </p>
          ) : (
            <>
              {memberAiPersonas.length > 0 && (
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-3">
                    Your organization&apos;s AI personas
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {memberAiPersonas.map((persona) => (
                      <div
                        key={persona.id}
                        className="bg-white p-5 rounded-lg border border-green-200"
                      >
                        <div className="flex items-start justify-between mb-2 gap-2">
                          <div className="min-w-0">
                            <h3 className="font-semibold text-gray-900 break-words">
                              {persona.name}
                            </h3>
                            <p className="text-sm text-gray-500 truncate">{persona.jobTitle}</p>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <PersonaAiBadge />
                            <span
                              className={`text-xs px-2 py-1 rounded ${typePillClass(persona.type)}`}
                            >
                              {persona.type}
                            </span>
                          </div>
                        </div>
                        <p className="text-sm text-gray-600">{persona.agenda}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {memberCrmPersonas.length > 0 && (
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-3">CRM-powered personas</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {memberCrmPersonas.map((persona) => (
                      <div
                        key={persona.id}
                        className="bg-white p-5 rounded-lg border border-blue-200"
                      >
                        <div className="flex items-start justify-between mb-2 gap-2">
                          <div className="min-w-0">
                            <h3 className="font-semibold text-gray-900 break-words">
                              {persona.name}
                            </h3>
                            <p className="text-sm text-gray-500 truncate">{persona.jobTitle}</p>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <CrmPersonaBadge source={persona.source ?? 'crm_aggregate'} />
                            <span
                              className={`text-xs px-2 py-1 rounded ${typePillClass(persona.type)}`}
                            >
                              {persona.type}
                            </span>
                          </div>
                        </div>
                        <p className="text-sm text-gray-600">{persona.agenda}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {memberCatalogPersonas.length > 0 && (
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-3">Platform catalog</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {memberCatalogPersonas.map((persona) => (
                      <div
                        key={persona.id}
                        className="bg-white p-5 rounded-lg border border-green-200"
                      >
                        <div className="flex items-start justify-between mb-2 gap-2">
                          <div className="min-w-0">
                            <h3 className="font-semibold text-gray-900 break-words">
                              {persona.name}
                            </h3>
                            <p className="text-sm text-gray-500 truncate">{persona.jobTitle}</p>
                          </div>
                          <span
                            className={`text-xs px-2 py-1 rounded shrink-0 ${typePillClass(persona.type)}`}
                          >
                            {persona.type}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">{persona.agenda}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {isAdmin && (
        <div className="mb-6 space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-sm text-amber-900 font-medium">Better inputs mean better personas</p>
            <p className="text-sm text-amber-800 mt-1">
              The more information you add about your company, product, sales motion, and
              competitors in your business profile, the more accurate your AI-generated personas
              will be. You can update your business profile any time after onboarding—then
              regenerate here.
            </p>
            {completeness && (
              <div className="mt-3 flex flex-wrap gap-2 items-center">
                {SECTION_LINKS.map(({ key, label }) => (
                  <Link
                    key={key}
                    to={`/onboarding?section=${key}`}
                    className={`text-xs px-2 py-1 rounded inline-block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-600 focus-visible:ring-offset-1 ${
                      completeness.sections[key]
                        ? 'bg-green-100 text-green-800 hover:underline'
                        : 'bg-white text-amber-900 border border-amber-300 hover:underline'
                    }`}
                  >
                    {completeness.sections[key] ? '✓ ' : 'Add '}
                    {label}
                  </Link>
                ))}
                <Link
                  to="/onboarding"
                  className="text-sm text-red-800 font-medium hover:underline ml-2"
                >
                  Open business profile
                </Link>
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => void handleGenerateClick()}
              disabled={generating}
              className="bg-red-700 text-white px-4 py-2 rounded-md font-medium text-sm hover:bg-red-800 disabled:opacity-50"
            >
              {generating
                ? 'Generating…'
                : hasAiPersonas
                  ? 'Regenerate with AI'
                  : 'Generate with AI'}
            </button>
          </div>
        </div>
      )}

      {isAdmin && aiPersonas.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Your AI-generated personas</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {aiPersonas.map((persona) => (
              <div
                key={persona.id}
                className={`bg-white p-5 rounded-lg border border-red-100 ${
                  persona.enabled ? 'border-green-200' : 'opacity-60'
                }`}
              >
                {editingId === persona.id && editForm && editMode === 'ai' ? (
                  <div className="space-y-2 text-sm">
                    <input
                      className="w-full border rounded px-2 py-1"
                      value={editForm.name}
                      onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    />
                    <input
                      className="w-full border rounded px-2 py-1"
                      value={editForm.jobTitle}
                      onChange={(e) => setEditForm({ ...editForm, jobTitle: e.target.value })}
                    />
                    <select
                      className="w-full border rounded px-2 py-1"
                      value={editForm.type}
                      onChange={(e) => setEditForm({ ...editForm, type: e.target.value })}
                    >
                      <option value="champion">Champion</option>
                      <option value="blocker">Blocker</option>
                      <option value="skeptic">Skeptic</option>
                      <option value="economic_buyer">Economic buyer</option>
                      <option value="technical_evaluator">Technical evaluator</option>
                      <option value="end_user">End user</option>
                    </select>
                    <textarea
                      className="w-full border rounded px-2 py-1"
                      rows={2}
                      value={editForm.agenda}
                      onChange={(e) => setEditForm({ ...editForm, agenda: e.target.value })}
                    />
                    <textarea
                      className="w-full border rounded px-2 py-1"
                      rows={2}
                      value={editForm.typicalObjections}
                      onChange={(e) =>
                        setEditForm({ ...editForm, typicalObjections: e.target.value })
                      }
                    />
                    <div>
                      <label className="block text-xs text-gray-600 mb-0.5">
                        Gender (optional)
                      </label>
                      <select
                        className="w-full border rounded px-2 py-1"
                        value={editForm.gender ?? ''}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            gender: e.target.value === '' ? null : e.target.value,
                          })
                        }
                      >
                        <option value="">Not specified</option>
                        <option value="female">Female</option>
                        <option value="male">Male</option>
                        <option value="nonbinary">Non-binary</option>
                      </select>
                    </div>
                    <PersonaVoiceVideoFields
                      voice={editForm.voice ?? null}
                      voiceProvider={editForm.voiceProvider ?? null}
                      videoAvatarId={editForm.videoAvatarId ?? null}
                      voiceProviderName={voiceProvider}
                      voiceOptions={voiceOptions}
                      videoAvatars={videoAvatars}
                      voiceFieldId={`ai-persona-voice-${persona.id}`}
                      videoFieldId={`ai-persona-video-${persona.id}`}
                      onChange={(patch) => setEditForm({ ...editForm, ...patch })}
                    />
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => void saveEdit()}
                        className="text-red-700 text-sm font-medium"
                      >
                        Save
                      </button>
                      <button type="button" onClick={cancelEdit} className="text-gray-600 text-sm">
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-start justify-between mb-2 gap-2">
                      <div className="min-w-0">
                        <h3 className="font-semibold text-gray-900 break-words">{persona.name}</h3>
                        <p className="text-sm text-gray-500 truncate">{persona.jobTitle}</p>
                      </div>
                      <span className="text-xs px-2 py-1 bg-red-50 text-red-800 rounded shrink-0">
                        AI
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{persona.agenda}</p>
                    <p className="text-xs text-gray-400 mb-3">{persona.typicalObjections}</p>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => startEditAi(persona)}
                        className="text-sm text-blue-600 hover:underline"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => setPersonaIdToDelete(persona.id)}
                        className="text-sm text-red-600 hover:underline"
                      >
                        Delete
                      </button>
                      <button
                        type="button"
                        onClick={() => togglePersona(persona.id, persona.enabled)}
                        className={`text-sm font-medium ${
                          persona.enabled
                            ? 'text-red-600 hover:text-red-700'
                            : 'text-green-600 hover:text-green-700'
                        }`}
                      >
                        {persona.enabled ? 'Disable' : 'Enable'}
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {isAdmin && crmConnected && (
        <div className="mb-8">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-gray-900">CRM-powered personas</h2>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => void handleAggregateGenerate()}
                disabled={generatingAggregate}
                className="px-3 py-1.5 text-sm text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {generatingAggregate ? 'Generating...' : 'Generate from CRM data'}
              </button>
              {hasDealFeature && (
                <button
                  type="button"
                  onClick={() => setShowDealPicker(!showDealPicker)}
                  className="px-3 py-1.5 text-sm text-blue-700 border border-blue-200 rounded hover:bg-blue-50"
                >
                  {showDealPicker ? 'Hide deal picker' : 'Practice for a deal'}
                </button>
              )}
            </div>
          </div>

          {showDealPicker && (
            <div className="mb-6 p-4 bg-gray-50 rounded-lg border">
              <h3 className="text-sm font-medium text-gray-700 mb-3">
                Select a deal to generate buyer personas
              </h3>
              <CrmDealPicker
                onPersonasSaved={() => {
                  loadPersonas().catch(() => {});
                  setShowDealPicker(false);
                }}
              />
            </div>
          )}

          {crmPersonas.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {crmPersonas.map((persona) => (
                <div
                  key={persona.id}
                  className={`bg-white p-5 rounded-lg border ${persona.enabled ? 'border-blue-200' : 'border-gray-200 opacity-60'}`}
                >
                  {editingId === persona.id && editForm && editMode === 'crm' ? (
                    <div className="space-y-2 text-sm">
                      <p className="font-semibold text-gray-900">{persona.name}</p>
                      <p className="text-xs text-gray-500">{persona.jobTitle}</p>
                      <PersonaVoiceVideoFields
                        voice={editForm.voice ?? null}
                        voiceProvider={editForm.voiceProvider ?? null}
                        videoAvatarId={editForm.videoAvatarId ?? null}
                        voiceProviderName={voiceProvider}
                        voiceOptions={voiceOptions}
                        videoAvatars={videoAvatars}
                        voiceFieldId={`crm-persona-voice-${persona.id}`}
                        videoFieldId={`crm-persona-video-${persona.id}`}
                        onChange={(patch) => setEditForm({ ...editForm, ...patch })}
                      />
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => void saveEdit()}
                          className="text-blue-700 text-sm font-medium"
                        >
                          Save
                        </button>
                        <button
                          type="button"
                          onClick={cancelEdit}
                          className="text-gray-600 text-sm"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-start justify-between mb-2 gap-2">
                        <div className="min-w-0">
                          <h3 className="font-semibold text-gray-900 break-words">
                            {persona.name}
                          </h3>
                          <p className="text-sm text-gray-500 truncate">{persona.jobTitle}</p>
                        </div>
                        <CrmPersonaBadge source={persona.source ?? 'crm_aggregate'} />
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{persona.agenda}</p>
                      <p className="text-xs text-gray-400 mb-3">{persona.typicalObjections}</p>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => startEditCrm(persona)}
                          className="text-sm text-blue-600 hover:underline"
                        >
                          Edit voice &amp; avatar
                        </button>
                        <button
                          type="button"
                          onClick={() => togglePersona(persona.id, persona.enabled)}
                          className={`text-sm font-medium ${persona.enabled ? 'text-red-600 hover:text-red-700' : 'text-green-600 hover:text-green-700'}`}
                        >
                          {persona.enabled ? 'Disable' : 'Enable'}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
          {crmPersonas.length === 0 && !showDealPicker && (
            <p className="text-sm text-gray-500">
              No CRM personas yet. Generate them from your CRM data above.
            </p>
          )}
        </div>
      )}

      {isAdmin && platformPersonas.length > 0 && (
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Platform catalog</h2>
        </div>
      )}

      {isAdmin && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {platformPersonas.map((persona) => (
            <div
              key={persona.id}
              className={`bg-white p-5 rounded-lg border ${
                persona.enabled ? 'border-green-200' : 'border-gray-200 opacity-60'
              }`}
            >
              <div className="flex items-start justify-between mb-2 gap-2">
                <div className="min-w-0">
                  <h3 className="font-semibold text-gray-900 break-words">{persona.name}</h3>
                  <p className="text-sm text-gray-500 truncate">{persona.jobTitle}</p>
                </div>
                <span
                  className={`text-xs px-2 py-1 rounded shrink-0 ${typePillClass(persona.type)}`}
                >
                  {persona.type}
                </span>
              </div>
              <p className="text-sm text-gray-600 mb-3">{persona.agenda}</p>
              <button
                type="button"
                onClick={() => togglePersona(persona.id, persona.enabled)}
                className={`text-sm font-medium ${
                  persona.enabled
                    ? 'text-red-600 hover:text-red-700'
                    : 'text-green-600 hover:text-green-700'
                }`}
              >
                {persona.enabled ? 'Disable' : 'Enable'}
              </button>
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={pendingGenerate}
        title="Replace AI-generated personas?"
        description="This replaces your current AI-generated personas with a new set."
        confirmLabel="Continue"
        onConfirm={() => {
          setPendingGenerate(false);
          void runGenerateAfterConfirm();
        }}
        onCancel={() => setPendingGenerate(false)}
        loading={generating}
      />
      <ConfirmDialog
        open={personaIdToDelete !== null}
        title="Delete persona?"
        description="Delete this AI-generated persona? This cannot be undone."
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={() => void confirmDeleteAiPersona()}
        onCancel={() => setPersonaIdToDelete(null)}
      />
      <PersonaGeneratePreview
        open={previewOpen}
        personas={previewPersonas}
        completeness={previewCompleteness}
        onClose={() => setPreviewOpen(false)}
        onConfirmSave={(rows) => handleConfirmSave(rows)}
        onRegenerate={handlePreviewRegenerate}
        isSaving={saving}
      />
    </div>
  );
}
